import { BidText, BidToken } from '../common/BidToken';
import { CorrectionEditor } from './CorrectionEditor';
import type { PredictedJeffBid, PredictionDivergence } from '../../types/predictedJeffBids';

/**
 * The explanation row beneath the table frame (feature 025-ai-predicted-bids): why the AI made
 * the call the reviewer just clicked, beside the editor's correction for a wrong prediction.
 *
 * Two things can appear here, each independent of the other:
 *
 *   1. The reasoning for the selected bid. Absent until a bid is selected, and explicitly
 *      "none recorded" for a call the engine did not justify — never the previous bid's text,
 *      which would quietly attribute one call's reasoning to another.
 *   2. The editor's correction, on mismatched hands only. An exact match has nothing to correct.
 *
 * The engine's own divergence write-up, its caveats, and partner's predicted calls used to show
 * here too; the client dropped them (2026-07-24) — this surface now carries only the per-bid
 * reasoning and the editor's note. The AI Prediction panel still marks each call right/wrong and
 * flags a differing prediction, so the mismatch itself stays visible without them.
 */

export interface BidExplanationPanelProps {
  /** The selected bid, or null when the reviewer has not clicked one yet. */
  selectedBid: PredictedJeffBid | null;
  /**
   * Divergence details; null on an exact match. No longer rendered — kept only to gate the
   * correction editor, which belongs to a mismatched hand.
   */
  divergence: PredictionDivergence | null;
  /** The hand whose correction the editor is writing (keys the correction editor's draft). */
  tableResultId: number;
  /** The shared editor correction on record for this hand, or null when none. */
  correctionExplanation: string | null;
  /** Persists the correction; null clears it. Rejects on failure so the editor keeps the draft. */
  onSaveCorrection: (correction: string | null) => Promise<void>;
  /** Reports whether the correction draft is unsaved, so the page can guard navigation. */
  onCorrectionDirtyChange?: (dirty: boolean) => void;
  /** Set when the page has blocked a navigation because of an unsaved correction draft. */
  remindUnsavedCorrection?: boolean;
}

export function BidExplanationPanel({
  selectedBid,
  divergence,
  tableResultId,
  correctionExplanation,
  onSaveCorrection,
  onCorrectionDirtyChange,
  remindUnsavedCorrection,
}: BidExplanationPanelProps) {
  return (
    <div
      className="h-full rounded-[10px] bg-cream px-[18px] py-4 text-ink shadow-[0_2px_6px_rgba(0,0,0,.25)]"
      data-testid="bid-explanation"
    >
      {/* One column: the AI's reasoning for the selected call, then the editor's correction
          beneath it. The panel sits under the hands and the auction only, beside the AI
          prediction column (user, 2026-10-09), which leaves too little width for two columns.
          `h-full` lets the card fill the row when the prediction column beside it is longer. */}
      <div className="grid gap-y-4">
        {/* Left column: the AI's reasoning for the selected call. */}
        <div>
          <h2 className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.09em] text-ink-soft">
            Why the AI bid this
          </h2>

          {selectedBid === null ? (
            <p className="text-[14px] text-ink-soft" data-testid="explanation-none-selected">
              Select one of the AI&apos;s calls to read its reasoning.
            </p>
          ) : (
            <div data-testid="explanation-body">
              <p className="mb-1.5 text-[15px] font-bold">
                <BidToken bid={selectedBid.bid} />
                <span className="ml-2 font-normal text-ink-soft">
                  (call {selectedBid.ordinal})
                </span>
              </p>
              {selectedBid.reason ? (
                <p className="text-[14.5px] leading-relaxed" data-testid="explanation-reason">
                  <BidText text={selectedBid.reason} />
                </p>
              ) : (
                <p className="text-[14.5px] italic text-ink-soft" data-testid="explanation-missing">
                  No explanation was recorded for this call.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Right column: the editor's correction. Belongs to the divergence, so it only appears
            on mismatched hands; it opens without a divider rule. */}
        {divergence && (
          <CorrectionEditor
            tableResultId={tableResultId}
            correction={correctionExplanation}
            onSave={onSaveCorrection}
            onDirtyChange={onCorrectionDirtyChange}
            remindUnsaved={remindUnsavedCorrection}
            className=""
          />
        )}
      </div>
    </div>
  );
}
